/* eslint-disable max-len */
import React, { useMemo, useCallback, useState, useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { withScope } from '@bingads-webui-react/scope-service';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withSiteMap, processor } from '@bingads-webui-campaign-react/with-site-map';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useLock, keyVault, priorityLevel } from '@bingads-webui-react/popup-manager';
import { withCcJsData } from '@bingads-webui-cc-react/js-data-rest';
import { Constants as CCUIConstants } from '@bingads-webui-clientcenter/entity-utils';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { TeachingBubble } from '@bingads-webui-campaign-react/popup-components';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';

import { CalloutCardComponent } from './component';
import { getLogger } from './logger';

export const CalloutPreferenceFlag = 'WebApp.CampaignUI.ShowConversionTrackingCouponCallout';

export const CalloutDisplayPeriod = {
  AfterClickClose: 7,
  // AfterClickCancelButton: 2,
};

const FAC_FEATUREID = 208;
const FAC_STATUS_NOT_IN_PROGRESS = CCUIConstants.RecommendationAdoptionStatus.NotInProgess;

const ConversionTrackingCouponCalloutInternal = ({
  preferenceService,
  onNext,
  scope,
  siteMap,
  activity,
  ccJsData,
  serverContext,
}) => {
  const [show, setShow] = useState(false);
  const i18n = useContext(I18nContext);

  const logger = useMemo(() => getLogger(activity), [activity]);

  const [coupon, setCoupon] = useState(null);
  const [couponsFetched, setCouponsFetched] = useState(false);
  const couponAcknowledged = useRef(false);
  const navPermssions = { dynamic: { NavigationRefresh: serverContext.DynamicPermissions && serverContext.DynamicPermissions.NavigationRefresh } };
  const isNavRefresh = isNavigationRefreshOn(navPermssions);

  const targetElement = useMemo(() => (
    isNavRefresh
      ? document.querySelector('.new-global-menu button#help-menu-button')
      : document.querySelector('.global-menu-toolbar #tools')
  ), [isNavRefresh]);

  const { lock, releaseLock } = useLock({
    key: keyVault.conversionCoupon,
    priority: priorityLevel.teachingBubble,
    isReady: show,
  });

  useEffect(() => {
    if (scope.values.HideCouponCallout) {
      onNext();
      return;
    }

    if (couponsFetched || couponAcknowledged.current || !scope.values.aid) {
      return;
    }
    ccJsData.FeatureAdoptionCoupon
      .findAll({ aid: scope.values.aid })
      .then((coupons) => {
        if (!_.isArray(coupons) || coupons.length === 0) {
          logger.logTrace('Conversion coupon: Not shown because no coupons returned from MT');
          return [];
        }
        logger.logTrace(`Conversion coupon: Following coupons were returned: ${JSON.stringify(_.map(coupons, mtCoupon => _.pick(mtCoupon, ['Status', 'FeatureId'])))}`);
        return coupons;
      })
      .catch((err) => {
        logger.logError(err);
        return [];
      })
      .then(coupons => _.filter(
        coupons,
        ({ Status, FeatureId }) =>
          FeatureId === FAC_FEATUREID && Status === FAC_STATUS_NOT_IN_PROGRESS
      ))
      .then((coupons) => {
        if (coupons.length > 0) {
          const serverCoupon = coupons[0];
          setCoupon(serverCoupon);
        } else {
          logger.logTrace('Conversion coupon: Not shown because no matching coupon found after filter');
        }
        setCouponsFetched(true);
      });
  }, [ccJsData.FeatureAdoptionCoupon, scope.values.aid, ccJsData, i18n, serverContext.CurrentAccount.CurrencyCode, coupon, couponsFetched, scope.values.HideCouponCallout, onNext, logger]);

  useEffect(() => {
    if (!couponsFetched) {
      return;
    }

    if (couponAcknowledged.current || !coupon) {
      onNext();
      return;
    }

    const bubblePref = preferenceService.findByNameAtUserLevel(CalloutPreferenceFlag);
    let showBubble = true;

    if (bubblePref && bubblePref.Data) {
      if (bubblePref.Data.hasTry) {
        logger.logTrace('Conversion coupon: Not shown because user has already clicked Ok');
        showBubble = false;
      } else {
        const { period, timestamp } = bubblePref.Data;
        if (period > 0) {
          const currentTime = new Date().getTime();
          const diffDay = (currentTime - timestamp) / 1000 / 60 / 60 / 24;
          showBubble = diffDay > period;
          if (!showBubble) {
            logger.logTrace(`Conversion coupon: Not shown because dismissal too recent. Dismissed ${diffDay} days ago, minimum required ${period} days`);
          }
        } else {
          showBubble = true;
        }
      }
    }

    setShow(showBubble);
    if (showBubble) {
      logger.logTrace('Conversion coupon callout show');
    } else {
      onNext();
    }
  }, [coupon, couponsFetched, logger, onNext, preferenceService]);

  const handlePreferenceData = useCallback((hasTry, period = -1) => {
    const timestamp = new Date().getTime();
    preferenceService.setAtUserLevel({
      Name: CalloutPreferenceFlag,
      Data: {
        hasTry,
        period,
        timestamp,
      },
    });
  }, [preferenceService]);

  const gotoConversionGoals = useCallback(() => {
    const pageItem = _.findWhere(siteMap, { id: 'conversion-goals' });
    const pathname = `/campaign/vnext/${pageItem.route}`;
    const params = {
      ...scope.values,
    };
    const search = _.reduce(params, (result, value, key) => `${result}&${key}=${value}`, '');
    if (!_.isEmpty(pageItem)) {
      const { history } = scope;
      history.replace({
        pathname,
        search: `?${search}`,
      });
    }
  }, [scope, siteMap]);

  const onConfirm = useCallback(() => {
    couponAcknowledged.current = true;

    setShow(false);
    releaseLock();
    handlePreferenceData(true);
    gotoConversionGoals();
    logger.logTrace('Conversion coupon: click confirm in callout');
    onNext();
  }, [releaseLock, gotoConversionGoals, handlePreferenceData, logger, onNext]);

  const onDismiss = useCallback(() => {
    couponAcknowledged.current = true;

    setShow(false);
    releaseLock();
    handlePreferenceData(false, CalloutDisplayPeriod.AfterClickClose);
    logger.logTrace('Conversion coupon: click close in callout');
    onNext();
  }, [releaseLock, handlePreferenceData, logger, onNext]);

  const buttons = useMemo(() => [
    { isPrimary: true, label: i18n.getString(_TL_('Set up conversion tracking')), onClick: onConfirm },
    { label: i18n.getString(_TL_('Dismiss')), onClick: onDismiss },
  ], [i18n, onConfirm, onDismiss]);

  // only show it in account level
  if (!scope.values || !scope.values.aid) {
    onNext();
    return null;
  }

  if (_.result(serverContext, ['Permissions', 'IsCentralizedPopupManagementEnabled'])) {
    const contentNode = coupon ? (
      <div className="conversion-coupon-callout-card-content">
        {
          coupon ?
            <div className="conversion-coupon-callout-card-coupon">
              <span>
                {curlyTemplate(i18n.getString(_TL_('Receive {{couponValue}} Credit')))({ couponValue: i18n.formatCurrency(coupon.CouponValue, serverContext.CurrentAccount.CurrencyCode) })}
              </span>
            </div> : <></>
        }
        <span>
          {curlyTemplate(i18n.getString(_TL_('Get the chance to receive {{couponValue}} in credit when you set up conversion tracking. Conversion tracking helps you track the effectiveness of your ads and measure the return on investment of your ad campaign.')))({ couponValue: i18n.formatCurrency(coupon.CouponValue, serverContext.CurrentAccount.CurrencyCode) })}
        </span>
      </div>
    ) : null;

    return show ? (
      <TeachingBubble
        featureName="conversion-callout-card"
        title={i18n.getString(_TL_('Set up conversion tracking'))}
        content={contentNode}
        onDone={buttons[0].onClick}
        onDismiss={buttons[1].onClick}
        onClose={buttons[1].onClick}
        primaryButtonText={buttons[0].label}
        secondaryButtonText={buttons[1].label}
        targetElement={targetElement}
        lockKey={keyVault.conversionCoupon}
      />
    ) : null;
  }

  return lock ? (
    <CalloutCardComponent
      i18n={i18n}
      title={i18n.getString(_TL_('Set up conversion tracking'))}
      content={curlyTemplate(i18n.getString(_TL_('Get the chance to receive {{couponValue}} in credit when you set up conversion tracking. Conversion tracking helps you track the effectiveness of your ads and measure the return on investment of your ad campaign.')))({ couponValue: i18n.formatCurrency(coupon.CouponValue, serverContext.CurrentAccount.CurrencyCode) })}
      buttons={buttons}
      target={targetElement}
      coupon={coupon}
      currency={serverContext.CurrentAccount.CurrencyCode}
    />
  ) : null;
};

ConversionTrackingCouponCalloutInternal.propTypes = {
  preferenceService: PropTypes.shape({
    setAtUserLevel: PropTypes.func,
    findByNameAtUserLevel: PropTypes.func,
  }).isRequired,
  onNext: PropTypes.func.isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({ aid: PropTypes.number.isRequired, HideCouponCallout: PropTypes.bool }),
  }).isRequired,
  siteMap: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  activity: PropTypes.shape({
    trace: PropTypes.func.isRequired, error: PropTypes.func.isRequired,
  }).isRequired,
  ccJsData: PropTypes.shape({
    FeatureAdoptionCoupon: PropTypes.shape({
      findAll: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  serverContext: PropTypes.shape({
    DynamicPermissions: PropTypes.shape({
      NavigationRefresh: PropTypes.bool.isRequired,
    }),
    CurrentAccount: PropTypes.shape({
      CurrencyCode: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export const ConversionTrackingCouponCallout = withActivityContext(withCcJsData(withServerContext(withScope(withSiteMap(withScope(ConversionTrackingCouponCalloutInternal), [processor.processPath])))));
